/**
 * Optimistic Finance state transitions for chat-driven mutations (Finance Lag #438).
 *
 * WHY THIS EXISTS. When PAI deletes or moves a transaction (through the chat card,
 * a tool call, or a Telegram turn) an open Finance panel only ever heard
 * "something changed — refetch". The refetch is the slow request in the module
 * (`GET /api/expenses` and `/books` FX-convert every row, and block on an upstream
 * rate miss), so the row lingered for seconds — or, on the card path, for good,
 * because nothing listened to the event that path fired. The event itself already
 * says WHICH transaction changed; these pure reducers apply that to the panel's
 * state at once and the normal refetch merely RECONCILES afterwards.
 *
 * Pure and framework-free so the rules are unit-tested without a DOM:
 *   state = { expenses, books, unassigned }   (the panel's three pieces of state)
 * Every function returns the SAME `state` object when nothing applies, so a
 * caller can tell "changed" from "no-op" by identity (and then bump its
 * last-issued-wins request counters, #294).
 *
 * TOTALS. A book's figures are server-converted into ONE currency (`stats.currency`).
 * A row's own `amount` is in ITS currency, so a total is adjusted only when the two
 * match — otherwise only the counts move and the reconcile refetch corrects the
 * figure. Showing a quietly wrong total would be worse than a briefly stale one.
 */

const UNASSIGNED = 'unassigned';
const cents = (n) => Math.round(n * 100) / 100;

const sameCurrency = (a, b) => !!a && !!b && String(a).toUpperCase() === String(b).toUpperCase();

/** A stats block (book or Unassigned) after adding (+1) / removing (-1) one row. */
export function adjustStats(stats, { direction, amount, currency }, sign) {
    if (!stats) return stats;
    const income = direction === 'income';
    const countKey = income ? 'income_count' : 'expense_count';
    const totalKey = income ? 'income_total' : 'expense_total';
    const next = { ...stats, [countKey]: Math.max(0, (Number(stats[countKey]) || 0) + sign) };

    const amt = Number(amount);
    if (Number.isFinite(amt) && sameCurrency(stats.currency, currency)) {
        next[totalKey] = Math.max(0, cents((Number(stats[totalKey]) || 0) + sign * amt));
        const inc = Number(next.income_total) || 0;
        const exp = Number(next.expense_total) || 0;
        next.net = cents(inc - exp);
        next.total_amount = cents(inc + exp);
    }
    return next;
}

/** Apply `adjustStats` to whichever stats block owns `bookKey` ('unassigned' | book id). */
function adjustOwner(state, bookKey, row, sign) {
    if (bookKey === UNASSIGNED) {
        return { ...state, unassigned: adjustStats(state.unassigned, row, sign) };
    }
    if (!state.books.some((b) => b.id === bookKey)) return state;
    return {
        ...state,
        books: state.books.map((b) => (b.id === bookKey ? adjustStats(b, row, sign) : b)),
    };
}

const idOf = (detail) => detail?.id ?? detail?.expense_id ?? null;

/**
 * A transaction was deleted. Removes it from the open list and takes it out of its
 * book's counts/totals. Needs the row to be in the list OR the event to carry
 * `direction` and a `book_id` key (the in-stream event does; the cross-channel
 * feed carries identity only, so a row that is not on screen is left to the
 * reconcile refetch).
 */
export function removeExpense(state, detail) {
    const id = idOf(detail);
    if (!id) return state;
    const row = state.expenses.find((e) => e.id === id);

    const direction = row?.direction ?? detail.direction;
    let bookKey = null;                      // null = not resolvable
    if (row) bookKey = row.book_id ?? UNASSIGNED;
    else if (detail && detail.book_id !== undefined) bookKey = detail.book_id ?? UNASSIGNED;

    let next = state;
    if (row) next = { ...next, expenses: next.expenses.filter((e) => e.id !== id) };
    if (direction && bookKey) {
        next = adjustOwner(next, bookKey, {
            direction,
            amount: row ? row.amount : detail.amount,
            currency: row?.currency ?? detail.currency,
        }, -1);
    }
    return next;
}

/**
 * A transaction was updated (moved to another book, re-classified, made recurring,
 * or given a receipt document — #440).
 * Only applies to a row that is on screen — it needs the row's own amount to move
 * the totals correctly; anything else is the reconcile refetch's job.
 *
 * `selectedKey` is the open view ('unassigned' | book id): a row that moved OUT of
 * it leaves the list.
 */
export function updateExpense(state, detail, selectedKey) {
    const id = idOf(detail);
    if (!id) return state;
    const row = state.expenses.find((e) => e.id === id);
    if (!row) return state;

    const oldKey = row.book_id ?? UNASSIGNED;
    let newKey;                              // undefined = the event says nothing about the book
    if (detail.book_key !== undefined) newKey = detail.book_key || UNASSIGNED;
    else if (detail.book_id !== undefined) newKey = detail.book_id ?? UNASSIGNED;

    const oldDirection = row.direction;
    const newDirection = detail.direction || oldDirection;

    const patch = {};
    if (newKey !== undefined && newKey !== oldKey) {
        patch.book_id = newKey === UNASSIGNED ? null : newKey;
        patch.book_name = newKey === UNASSIGNED
            ? null
            : (detail.book_name ?? state.books.find((b) => b.id === newKey)?.name ?? row.book_name ?? null);
    }
    if (newDirection !== oldDirection) patch.direction = newDirection;
    if (Object.prototype.hasOwnProperty.call(detail, 'recurrence')) {
        patch.recurrence = detail.recurrence || null;
        patch.is_recurring = !!detail.recurrence && !row.recurrence_parent_id;
    }
    // A receipt/invoice was linked to an existing row (match_documents_to_expenses,
    // #440) — the box shows its document the moment the event lands.
    if (Object.prototype.hasOwnProperty.call(detail, 'document_id')
        && (detail.document_id || null) !== (row.document_id || null)) {
        patch.document_id = detail.document_id || null;
    }
    // A re-categorisation (update_expense_categories, #449) — the row's chip
    // changes at once; the charts re-fetch because `expenses` changed.
    if (Object.prototype.hasOwnProperty.call(detail, 'category')
        && (detail.category || null) !== (row.category || null)) {
        patch.category = detail.category || null;
    }
    // An in-place edit of the row's details (update_expense, #511) — the same
    // row (receipt, items) shows its new date / amount / currency / merchant.
    for (const field of ['expense_date', 'merchant', 'description', 'currency']) {
        if (Object.prototype.hasOwnProperty.call(detail, field)
            && (detail[field] ?? null) !== (row[field] ?? null)) {
            patch[field] = detail[field] ?? null;
        }
    }
    if (Object.prototype.hasOwnProperty.call(detail, 'amount')
        && Number(detail.amount) !== Number(row.amount)) {
        patch.amount = Number(detail.amount);
    }
    if (!Object.keys(patch).length) return state;

    const figures = { amount: row.amount, currency: row.currency };
    const newFigures = {
        amount: 'amount' in patch ? patch.amount : row.amount,
        currency: 'currency' in patch ? patch.currency : row.currency,
    };
    let next = state;
    const bookChanged = 'book_id' in patch;
    if (bookChanged || 'direction' in patch || 'amount' in patch || 'currency' in patch) {
        next = adjustOwner(next, oldKey, { ...figures, direction: oldDirection }, -1);
        next = adjustOwner(next, bookChanged ? newKey : oldKey, { ...newFigures, direction: newDirection }, +1);
    }

    const landsInView = (patch.book_id !== undefined ? patch.book_id : row.book_id) ?? UNASSIGNED;
    next = {
        ...next,
        expenses: landsInView === selectedKey
            ? next.expenses.map((e) => (e.id === id ? { ...e, ...patch } : e))
            : next.expenses.filter((e) => e.id !== id),
    };
    return next;
}

/**
 * A book was renamed (Finance book name change #464 — the chat card, the
 * rename_book tool's typed-yes path, or a Telegram [Confirm] tap). The
 * book's tab and every on-screen row that shows its name pick up the new name at
 * once; the book id never changes, so nothing moves and no total is touched.
 * Needs `book_id` + the new name (`book_name`, or `new_name`).
 */
export function renameBook(state, detail) {
    const bookId = detail?.book_id;
    const name = detail?.book_name ?? detail?.new_name;
    if (!bookId || !name) return state;
    const book = state.books.find((b) => b.id === bookId);
    const rowsToFix = state.expenses.some((e) => e.book_id === bookId && e.book_name !== name);
    if ((!book || book.name === name) && !rowsToFix) return state;
    return {
        ...state,
        books: book && book.name !== name
            ? state.books.map((b) => (b.id === bookId ? { ...b, name } : b))
            : state.books,
        expenses: rowsToFix
            ? state.expenses.map((e) => (e.book_id === bookId ? { ...e, book_name: name } : e))
            : state.expenses,
    };
}
